import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  AutoAwesome,
  CheckCircle,
  CollectionsBookmark,
  CreditCard,
  FactCheck,
  Lock,
  LockOpen,
  Quiz,
  SportsEsports,
  Verified,
} from "@mui/icons-material";
import api from "../../api";
import useStudentPlanCheckout, {
  formatPlanPrice,
  isFreePlan,
} from "../../hooks/useStudentPlanCheckout";
import {
  formatListPrice,
  formatMoney,
  monthsInInterval,
  planAmountPence,
  planDurationLabel,
  offeredPlans as listOfferedPlans,
} from "../../utils/mobilePlanCatalogue";
import { renewalSentence, trialTermsShort } from "../../content/trialTerms";
import { formatTrialEnd } from "../../utils/mobileSubscriptionFormat";

const BRAND = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";

const poundsPerMonth = (plan) => {
  const months = monthsInInterval(plan?.billing_interval);
  const pence = planAmountPence(plan);
  if (!months || pence <= 0) return null;
  return pence / 100 / months;
};

/** What a plan adds on top of the free tier — the reason to buy, in the student's words. */
const PLAN_FEATURES = [
  { icon: FactCheck, text: "Every worksheet and AI-marked test" },
  { icon: Quiz, text: "All mock exams and practice papers included" },
  { icon: AutoAwesome, text: "Wizard: AI tutoring, marking and writing coach" },
  { icon: CollectionsBookmark, text: "Flash cards and quiz-style revision" },
  { icon: SportsEsports, text: "Games and the leaderboard" },
  { icon: Verified, text: "Progress dashboard across every subject" },
];

/**
 * The Mobile Plan tab for a student whose app plan has ended but who has bought a course,
 * mock exam or past paper. They keep working in the portal, so this is a plan page — what a
 * plan adds and what it costs — rather than the full-screen lock that stops everything.
 *
 * Students with nothing bought see StudentSubscriptionLock instead: routes.js decides.
 */
const MobilePlan = ({ lock }) => {
  const [planState, setPlanState] = useState(null);

  const {
    plans,
    trialTerms,
    selectedPlanId,
    setSelectedPlanId,
    preferredPlanId,
    loadingPlans,
    busy,
    fulfilling,
    error,
    openBillingPortal,
    continueWithPlan,
  } = useStudentPlanCheckout({
    preferredPlanId: lock?.preferredPlanId,
    redirectTo: "/student",
  });

  useEffect(() => {
    let cancelled = false;
    api
      .get("student/subscription/access")
      .then((res) => {
        if (!cancelled) setPlanState(res.data?.data ?? null);
      })
      .catch(() => {
        /* The page still works without it: it only softens the heading. */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // A consumed free trial cannot be started again, and a paid subscriber moves up rather than
  // down. Both rules now live in offeredPlans so every picker agrees; the hook has already
  // applied them, and this re-applies them against the richer access payload when it lands.
  // Blocked plans are shown greyed with the reason rather than hidden — a returning
  // customer who just sees the free trial vanish has no idea why.
  const planRows = useMemo(
    () =>
      listOfferedPlans({
        plans,
        renewal: planState?.renewal ?? null,
        canStartFreeTrial: planState ? planState.can_start_free_trial : undefined,
        currentPlanPricePence: Number(planState?.retained_price?.price_pence || 0),
      }),
    [plans, planState]
  );

  // Badges and the savings line only ever describe plans that can be chosen, or a
  // disabled free trial would win "Best value".
  const offeredPlans = useMemo(() => planRows.filter((plan) => plan.selectable), [planRows]);

  // Cheapest per month, for the "best value" badge and the savings line.
  const dearestPerMonth = useMemo(
    () =>
      offeredPlans.reduce((worst, plan) => {
        const rate = poundsPerMonth(plan);
        return rate && rate > worst ? rate : worst;
      }, 0),
    [offeredPlans]
  );

  const bestValueId = useMemo(() => {
    let best = null;
    let bestRate = Infinity;
    offeredPlans.forEach((plan) => {
      const rate = poundsPerMonth(plan);
      if (rate && rate < bestRate) {
        bestRate = rate;
        best = plan.id;
      }
    });
    return best;
  }, [offeredPlans]);

  if (fulfilling) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <Paper elevation={0} sx={{ p: 5, borderRadius: 4, textAlign: "center", maxWidth: 420 }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography fontWeight={700}>Confirming your payment…</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This only takes a moment.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const onTrial = planState?.is_trial === true || lock?.isTrial === true;
  const trialEndsLabel = formatTrialEnd(planState?.trial_ends_at || lock?.trialEndsAt);
  const endedOn = planState?.current_period_end
    ? new Date(planState.current_period_end).toLocaleDateString(undefined, {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;

  return (
    <Box sx={{ maxWidth: 1080, mx: "auto", px: { xs: 0, sm: 1 }, pb: 6 }}>
      {/* Hero: where the plan stands, and what is still open without one. */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          p: { xs: 3, md: 4 },
          mb: 3,
          background: BRAND,
          color: "#fff",
          boxShadow: "0 12px 34px rgba(102,126,234,0.35)",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
        >
          <Box sx={{ minWidth: 0 }}>
            <Chip
              size="small"
              label={
                onTrial && trialEndsLabel
                  ? `Free trial — ends ${trialEndsLabel}`
                  : onTrial
                  ? "Free trial"
                  : planState?.subscription_display_name || "No active plan"
              }
              sx={{
                mb: 1.5,
                color: "#fff",
                fontWeight: 700,
                backgroundColor: "rgba(255,255,255,0.22)",
              }}
            />
            <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              Unlock the full Mobile Plan
            </Typography>
            <Typography sx={{ mt: 1.5, opacity: 0.92, maxWidth: 620 }}>
              {onTrial
                ? `Your free trial ends ${trialEndsLabel || "soon"}. Subscribe to a plan and it starts as soon as payment succeeds.`
                : endedOn
                ? `Your plan ended on ${endedOn}. Everything you have already bought stays yours — a plan adds the rest.`
                : "Everything you have already bought stays yours — a plan adds the rest."}
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              minWidth: { md: 260 },
              backgroundColor: "rgba(255,255,255,0.16)",
              border: "1px solid rgba(255,255,255,0.3)",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <LockOpen sx={{ fontSize: 18 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                {onTrial ? "Open during your trial" : "Still open to you"}
              </Typography>
            </Stack>
            {(onTrial
              ? ["Mock exams and practice papers", "Worksheets, wizard, flash cards and games", "Your progress"]
              : ["Mock exams you bought", "Practice papers you bought", "Your courses and worksheets"]
            ).map(
              (line) => (
                <Stack key={line} direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
                  <CheckCircle sx={{ fontSize: 16, opacity: 0.9 }} />
                  <Typography variant="body2" sx={{ opacity: 0.95 }}>
                    {line}
                  </Typography>
                </Stack>
              )
            )}
          </Paper>
        </Stack>
      </Paper>

      {/* What the plan adds. */}
      <Paper elevation={0} sx={{ borderRadius: 4, p: { xs: 2.5, md: 3 }, mb: 3, border: "1px solid #e5e7eb" }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
          What a plan adds
        </Typography>
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
          }}
        >
          {PLAN_FEATURES.map(({ icon: Icon, text }) => (
            <Stack
              key={text}
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{
                p: 1.5,
                borderRadius: 2.5,
                backgroundColor: "#f8f9ff",
                border: "1px solid #eef0fb",
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: BRAND,
                }}
              >
                <Icon sx={{ color: "#fff", fontSize: 19 }} />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {text}
              </Typography>
            </Stack>
          ))}
        </Box>
      </Paper>

      {error ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      ) : null}

      {/* Plans. */}
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
        Choose your plan
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Cancel any time. Longer plans cost less per month.
      </Typography>

      {loadingPlans ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : planRows.length === 0 ? (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          No plans are available right now. Please contact support.
        </Alert>
      ) : (
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
          }}
        >
          {planRows.map((plan) => {
            const blocked = !plan.selectable;
            const active = !blocked && Number(plan.id) === Number(selectedPlanId);
            const preferred = !blocked && Number(plan.id) === Number(preferredPlanId);
            const bestValue = !blocked && Number(plan.id) === Number(bestValueId);
            const rate = poundsPerMonth(plan);
            const saving =
              rate && dearestPerMonth > 0 && rate < dearestPerMonth
                ? Math.round((1 - rate / dearestPerMonth) * 100)
                : 0;
            const free = isFreePlan(plan);

            return (
              <Paper
                key={plan.id}
                elevation={0}
                onClick={() => {
                  if (blocked) return;
                  setSelectedPlanId(plan.id);
                }}
                aria-disabled={blocked || undefined}
                sx={{
                  position: "relative",
                  p: 2.5,
                  borderRadius: 3.5,
                  cursor: blocked ? "not-allowed" : "pointer",
                  display: "flex",
                  flexDirection: "column",
                  opacity: blocked ? 0.55 : 1,
                  border: active ? "2px solid #667eea" : "1px solid #e5e7eb",
                  backgroundColor: blocked
                    ? "#f7f7f8"
                    : active
                      ? "rgba(102,126,234,0.04)"
                      : "#fff",
                  boxShadow: active ? "0 10px 28px rgba(102,126,234,0.22)" : "0 2px 10px rgba(0,0,0,0.05)",
                  transform: active ? "translateY(-2px)" : "none",
                  transition: "all 0.25s ease",
                  "&:hover": blocked
                    ? {}
                    : { borderColor: "#667eea", transform: "translateY(-2px)" },
                }}
              >
                {bestValue || preferred ? (
                  <Chip
                    size="small"
                    label={bestValue ? "Best value" : "Your selection"}
                    sx={{
                      position: "absolute",
                      top: 14,
                      right: 14,
                      fontWeight: 700,
                      color: "#fff",
                      background: BRAND,
                    }}
                  />
                ) : null}

                <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", pr: 9 }}>
                  {plan.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, minHeight: 40 }}>
                  {plan.description || (free ? "Free trial" : "Full mobile access")}
                </Typography>
                {/* What happens after the trial, or when this plan renews — stated before
                    the buyer picks, not only on the Stripe page. */}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, lineHeight: 1.6 }}>
                  {free ? trialTermsShort(trialTerms) : renewalSentence(plan)}
                </Typography>

                {/* The list price, struck through, whenever this viewer is being offered
                    less — an early-adopter discount is only persuasive if it is visible. */}
                {formatListPrice(plan) ? (
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", textDecoration: "line-through" }}
                  >
                    {formatListPrice(plan)}
                  </Typography>
                ) : null}
                <Typography sx={{ fontWeight: 900, fontSize: "1.9rem", color: "#4c3fbf", lineHeight: 1.1 }}>
                  {formatPlanPrice(plan)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {planDurationLabel(plan)}
                </Typography>
                <Box sx={{ minHeight: 26, mt: 0.5 }}>
                  {rate && monthsInInterval(plan.billing_interval) > 1 ? (
                    <Typography variant="caption" color="text.secondary">
                      {formatMoney(Math.round(rate * 100), plan.currency)} per month
                      {saving > 0 ? ` · save ${saving}%` : ""}
                    </Typography>
                  ) : null}
                </Box>

                {blocked ? (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 1, fontWeight: 700, color: "text.secondary" }}
                  >
                    {plan.blockedReason}
                  </Typography>
                ) : null}

                <Button
                  variant={active ? "contained" : "outlined"}
                  fullWidth
                  disabled={busy || blocked}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (blocked) return;
                    setSelectedPlanId(plan.id);
                    continueWithPlan(plan);
                  }}
                  startIcon={
                    busy && active ? (
                      <CircularProgress size={15} color="inherit" />
                    ) : free ? (
                      <CheckCircle />
                    ) : (
                      <CreditCard />
                    )
                  }
                  sx={{
                    mt: 2,
                    py: 1.1,
                    borderRadius: 2.5,
                    fontWeight: 700,
                    textTransform: "none",
                    ...(active
                      ? { background: BRAND, "&:hover": { background: BRAND, opacity: 0.92 } }
                      : { borderColor: "#c7c9f5", color: "#4c3fbf" }),
                  }}
                >
                  {free ? "Start free trial" : onTrial ? "Subscribe" : `Choose ${plan.name}`}
                </Button>
              </Paper>
            );
          })}
        </Box>
      )}

      <Divider sx={{ my: 4 }} />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Lock sx={{ fontSize: 18, color: "text.secondary" }} />
          <Typography variant="body2" color="text.secondary">
            Payments are handled securely by Stripe. Cancel any time.
          </Typography>
        </Stack>
        {lock?.portalAvailable ? (
          <Button
            variant="outlined"
            disabled={busy}
            onClick={() =>
              openBillingPortal(
                lock?.recoveryAction === "payment_method_update" ? "payment_method_update" : null
              )
            }
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2.5 }}
          >
            Update payment details
          </Button>
        ) : null}
      </Stack>
    </Box>
  );
};

export default MobilePlan;
